/**
 * GritCargo shared design tokens.
 *
 * The canonical home for the values DESIGN.md documents. This is the
 * brand-aligned product system derived from the GritCargo Brand Guidelines
 * v1.1: Deep Navy structural grounds, Signal Green as the primary-action /
 * selection accent, Open Sans headings + Inter body, pill primary CTAs, and
 * the rounded-square Signal Green icon-chip marker — applied with product
 * discipline (the operational status / severity / device-family semantics are
 * preserved verbatim, and color still always pairs with text or an icon).
 *
 * Prefer importing from here over hand-typing hex/px in `sx`. Feature modules
 * (e.g. Devices/constants/shipmentGroupTokens.ts) may re-export MOTION /
 * FOCUS_RING from this file so there is a single source of truth.
 *
 * Colors are the documented hex values; alpha "soft-tint" backgrounds are
 * derived via `softTint()` so the ~14% convention stays consistent.
 */

/** Raw palette values for non-CSS renderers (canvas charts, generated SVGs, map
 * marker data URLs). Normal DOM styling should use `COLORS` below so theme mode
 * changes flow through CSS variables. */
export const RAW_COLORS = {
  // ── Brand grounds ────────────────────────────────────────────────────────
  /** Deep Navy — the structural ground: navbars, headers, panels, footers, and
   *  prominent titles. Conveys permanence and stability. Also the focus ring. */
  navy: '#253045',
  /** Lighter navy — dividers, hover, and secondary panels sitting on navy. */
  navyTint: '#37526E',
  /** Darker navy — pressed / active navy states. */
  navyDeep: '#1B2433',

  // ── Signature accent / primary action ────────────────────────────────────
  /** Signal Green — primary CTA fill (green pill) AND selection / active state.
   *  Always carries a dark glyph or label on top; reserved for ~10% of a view. */
  signalGreen: '#B4EE2B',
  /** Ink that sits ON Signal Green (Cargo Black). Never light text on green. */
  signalGreenInk: '#0A0A0A',
  /** Slightly deeper green for the contained-button hover/pressed surface. */
  signalGreenHover: '#A6E015',
  /** Back-compat alias — identical hex to `signalGreen`. */
  accentLime: '#B4EE2B',

  // ── Ink / text ───────────────────────────────────────────────────────────
  ink: '#101828', // default text + headings fallback (navy used for prominence)
  cargoBlack: '#0A0A0A', // fine detail, iconography, max-contrast marks
  black: '#000000', // back-compat; NO LONGER the primary action (green pill is)
  onSurface: '#212121', // body copy on light grounds (brand on-surface)
  inkSecondary: '#636363', // secondary text / captions (brand neutral, ~4.7:1 AA)
  inkMuted: '#6B7280',
  inkFaint: '#9CA3AF',
  slate: '#98A2B3',

  // ── Surfaces ─────────────────────────────────────────────────────────────
  surfaceApp: '#F1F1F1', // app canvas (brand off-white)
  surfaceCard: '#FFFFFF', // raised content surfaces (cards, inputs, menus)
  surfaceTint: '#E7F0FF', // Mist Blue — subtle tinted sectioning surface
  surfaceRowHover: '#F7F8FA', // table / list row hover

  // ── Borders ──────────────────────────────────────────────────────────────
  borderStrong: '#C7C7C7', // input strokes (→ navy on hover/focus)
  borderSoft: '#E5E5E5', // outlined-button border
  hairlineHex: '#E4E6EA', // brand flat-hex hairline (rules, 1px separators)
  disabledBg: '#EEEEEE',

  // ── Semantic status (PRESERVED — operational + color-blind safety) ─────────
  // `*Ink` is the darkened, on-tint text variant.
  statusGood: '#34C759',
  statusGoodInk: '#1F9454',
  statusInfo: '#007AFF',
  statusInfoInk: '#0A5BD3', // darkened info-blue for text on white/soft-tint — AA ≥4.5:1
  statusCaution: '#FF9500',
  statusCautionSoft: '#FFC47C',
  statusCautionInk: '#A35200', // darkened caution for text on white/tint — 5.07:1 AA
  statusAlert: '#DD3333',
  statusAlertInk: '#B01C1C', // darkened alert for text on white/soft-tint — AA ≥4.5:1
  statusAlertOnDark: '#FF8A8A', // alert text on Deep Navy — AA ≥4.5:1
  statusGoodOnDark: '#83E29A', // good/transit text on Deep Navy — AA ≥4.5:1
  statusInfoOnDark: '#85B8FF', // info text on Deep Navy — AA ≥4.5:1
  warningAmber: '#D96908', // brand Alert Amber (warning/exception accent)
  warningAmberHover: '#C25E07', // hover for the amber (warning) button — the one amber-with-white-text case

  // ── Device-family hues (PRESERVED) — icon chip + uppercase micro-label ─────
  deviceImsense: '#1F9454',
  deviceImlock: '#1E6BB8',
  deviceImcontrol: '#5C3FC7',
  deviceImsecure: '#B5651D', // dull/earthy orange — muted enough to read apart from the vivid warning amber
} as const;

const cssColor = (name: string, fallback: string): string =>
  `var(--gc-color-${name}, ${fallback})`;

/** Core palette — see DESIGN.md §2 Colors. */
export const COLORS = {
  // ── Brand grounds / semantic ink ─────────────────────────────────────────
  /** Prominent content-plane action/heading color. In dark mode this lightens
   *  for contrast; use `frameNavy` for fixed navy structural grounds. */
  navy: cssColor('navy', RAW_COLORS.navy),
  navyTint: cssColor('navy-tint', RAW_COLORS.navyTint),
  navyDeep: cssColor('navy-deep', RAW_COLORS.navyDeep),
  /** Fixed structural navy frame for sidebars, tooltips, headers, and panels. */
  frameNavy: cssColor('frame-navy', RAW_COLORS.navy),
  frameNavyTint: cssColor('frame-navy-tint', RAW_COLORS.navyTint),
  frameNavyDeep: cssColor('frame-navy-deep', RAW_COLORS.navyDeep),
  onFrame: cssColor('on-frame', '#FFFFFF'),
  onAccent: cssColor('on-accent', '#FFFFFF'),

  // ── Signature accent / primary action ────────────────────────────────────
  signalGreen: RAW_COLORS.signalGreen,
  signalGreenInk: RAW_COLORS.signalGreenInk,
  signalGreenHover: RAW_COLORS.signalGreenHover,
  accentLime: RAW_COLORS.accentLime,

  // ── Ink / text ───────────────────────────────────────────────────────────
  ink: cssColor('ink', RAW_COLORS.ink),
  cargoBlack: RAW_COLORS.cargoBlack,
  black: RAW_COLORS.black,
  onSurface: cssColor('on-surface', RAW_COLORS.onSurface),
  inkSecondary: cssColor('ink-secondary', RAW_COLORS.inkSecondary),
  inkMuted: cssColor('ink-muted', RAW_COLORS.inkMuted),
  inkFaint: cssColor('ink-faint', RAW_COLORS.inkFaint),
  slate: cssColor('slate', RAW_COLORS.slate),

  // ── Surfaces ─────────────────────────────────────────────────────────────
  surfaceApp: cssColor('surface-app', RAW_COLORS.surfaceApp),
  surfaceCard: cssColor('surface-card', RAW_COLORS.surfaceCard),
  surfaceTint: cssColor('surface-tint', RAW_COLORS.surfaceTint),
  surfaceRowHover: cssColor('surface-row-hover', RAW_COLORS.surfaceRowHover),

  // ── Borders ──────────────────────────────────────────────────────────────
  borderStrong: cssColor('border-strong', RAW_COLORS.borderStrong),
  borderSoft: cssColor('border-soft', RAW_COLORS.borderSoft),
  hairlineHex: cssColor('hairline-hex', RAW_COLORS.hairlineHex),
  disabledBg: cssColor('disabled-bg', RAW_COLORS.disabledBg),

  // ── Semantic status (PRESERVED — operational + color-blind safety) ─────────
  statusGood: RAW_COLORS.statusGood,
  statusGoodInk: RAW_COLORS.statusGoodInk,
  statusInfo: RAW_COLORS.statusInfo,
  statusInfoInk: RAW_COLORS.statusInfoInk,
  statusCaution: RAW_COLORS.statusCaution,
  statusCautionSoft: RAW_COLORS.statusCautionSoft,
  statusCautionInk: RAW_COLORS.statusCautionInk,
  statusAlert: RAW_COLORS.statusAlert,
  statusAlertInk: RAW_COLORS.statusAlertInk,
  statusAlertOnDark: RAW_COLORS.statusAlertOnDark,
  statusGoodOnDark: RAW_COLORS.statusGoodOnDark,
  statusInfoOnDark: RAW_COLORS.statusInfoOnDark,
  warningAmber: RAW_COLORS.warningAmber,
  warningAmberHover: RAW_COLORS.warningAmberHover,

  // ── Device-family hues (PRESERVED) — icon chip + uppercase micro-label ─────
  deviceImsense: RAW_COLORS.deviceImsense,
  deviceImlock: RAW_COLORS.deviceImlock,
  deviceImcontrol: RAW_COLORS.deviceImcontrol,
  deviceImsecure: RAW_COLORS.deviceImsecure,
} as const;

/** Font roles — Open Sans for headings/display, Inter for body/UI, mono for the
 *  occasional eyebrow label (used sparingly, never above every section). Both
 *  Open Sans and Inter are loaded in public/index.html. */
export const FONTS = {
  heading: "'Open Sans', system-ui, -apple-system, sans-serif",
  body: "'Inter', 'Open Sans', system-ui, -apple-system, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, 'SF Mono', monospace",
} as const;

/** Type scale for shared primitives. Keep UI-library `fontSize` values here
 * instead of hand-typing rem/px literals per component. */
export const TYPE_SCALE = {
  micro: '0.6875rem',
  caption: '0.75rem',
  bodySm: '0.8125rem',
  body: '0.875rem',
  titleSm: '1rem',
  pageTitle: '1.5rem',
  // ── Modal-system type ramp (10–26px) — design_handoff_modals ──────────────
  nano: '0.625rem', // 10px
  captionLg: '0.78125rem', // 12.5px
  bodyMd: '0.84375rem', // 13.5px
  bodyLg: '0.90625rem', // 14.5px
  bodyXl: '0.9375rem', // 15px
  titleCompact: '1.1875rem', // 19px
  titleMd: '1.25rem', // 20px
  titleLg: '1.3125rem', // 21px
  titleXl: '1.375rem', // 22px
  title2Xl: '1.4375rem', // 23px
  displaySm: '1.625rem', // 26px
} as const;

/** Hairline borders / dividers — alpha-ink, not flat hex (DESIGN.md "Flat-At-Rest"). */
export const HAIRLINE = {
  card: 'rgba(16, 24, 40, 0.08)',
  divider: 'rgba(16, 24, 40, 0.06)',
  onNavy: 'rgba(255, 255, 255, 0.12)', // hairline on navy grounds
} as const;

/** Elevation — flat at rest; shadow only on hover or for floating layers. */
export const SHADOW = {
  hoverLift: '0 1px 3px rgba(16, 24, 40, 0.06)',
  floatingLayer: '0px 4px 20px rgba(0, 0, 0, 0.1)',
} as const;

/** Modal system tokens — design_handoff_modals (scrim, card, side-sheet, on-navy inks). */
export const MODAL = {
  scrim: 'rgba(12, 17, 28, 0.52)',
  scrimBlurPx: 5,
  cardShadow: '0 24px 60px rgba(10, 10, 10, 0.22), 0 8px 20px rgba(10, 10, 10, 0.10)',
  sheetShadow: '-16px 0 48px rgba(10, 10, 10, 0.2)',
  navyPanelShadow: '0 18px 48px rgba(37, 48, 69, 0.28)',
  radius: 18,
  radiusCompact: 16,
  fadeMs: 160,
  riseMs: 200,
  slideMs: 220,
  onNavySub: '#AEB6C2',
  onNavyMuted: 'rgba(255, 255, 255, 0.5)',
  onNavyPanel: 'rgba(255, 255, 255, 0.06)',
  onNavyHairline: 'rgba(255, 255, 255, 0.14)',
  onNavyCloseBg: 'rgba(255, 255, 255, 0.1)',
  onNavyDot: 'rgba(255, 255, 255, 0.28)',
} as const;

/** Corner radii (px). */
export const RADII = {
  chip: 10,
  iconChip: 12, // rounded-square Signal Green marker
  sm: 8, // inputs, menus, paper
  md: 12, // stat tiles, secondary controls
  lg: 14, // cards
  xl: 16, // dialogs, status pills
  xxl: 20, // dialog content inner
  pill: 9999, // primary CTAs and capsule badges
} as const;

/** Spacing scale (px). */
export const SPACING = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  /** Standard routed-page container padding (the app-wide de-facto value —
   *  off the 4px grid deliberately; use `${SPACING.page}px`, don't hand-type
   *  '20px'). */
  page: 20,
} as const;

/** Docked GritClaw side panel width (px). Single source for the panel Paper, the
 *  content inset it reserves (`--gritclaw-panel-inset`), and the command bar's
 *  re-centre offset, so all three stay in lockstep. */
export const GRITCLAW_PANEL_WIDTH = 420;
/** CSS custom property carrying the live panel inset (panel width when docked,
 *  else 0). Published by GritClawProvider; consumed by the main content margin
 *  and the command bar so they slide in sync with the drawer — no React churn. */
export const GRITCLAW_INSET_VAR = '--gritclaw-panel-inset';

/** Motion — fast, restrained, ease-out (DESIGN.md §4 / shipmentGroupTokens MOTION). */
export const MOTION = {
  easeOut: 'cubic-bezier(0.22, 1, 0.36, 1)',
  easeStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  /** Modal-system ease-out — "mechanical, no bounce" (design_handoff_modals). */
  easeMechanical: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
  durationShort: 160,
  durationMedium: 220,
} as const;

/** Keyboard focus indicator (DESIGN.md mandatory FOCUS_RING) — Deep Navy. */
export const FOCUS_RING = {
  outline: `2px solid ${COLORS.navy}`,
  outlineOffset: '2px',
} as const;

/** `sx`-spreadable focus-visible rule, e.g. `sx={{ ...focusVisible }}`. */
export const focusVisible = {
  '&:focus-visible': {
    outline: FOCUS_RING.outline,
    outlineOffset: FOCUS_RING.outlineOffset,
  },
} as const;

/** Reduced-motion media query key for `sx` (DESIGN.md requires a fallback). */
export const REDUCED_MOTION =
  '@media (prefers-reduced-motion: reduce)' as const;

/**
 * Shared list/row reveal — the single source for the staggered fade-up entrance
 * first shipped on the Shipments grouped cards, so every list and table reveals
 * its items the same way. A gentle 6px rise + fade, capped stagger so a long
 * list never makes the user wait.
 *
 * Usage: spread `revealKeyframes` onto the scroll/table container once (it
 * defines the `gcReveal` keyframe), then spread `revealItemSx(index)` onto each
 * item/row — but only when motion is allowed, e.g.
 *   sx={{ ...(prefersReducedMotion ? {} : revealItemSx(i)), ...rowSx }}
 * Gate on `prefersReducedMotion` (the item starts at opacity 0 inside the
 * keyframe, so it must never be applied when the animation can't run).
 */
export const REVEAL = {
  stepMs: 40,
  maxIndex: 9,
  durationMs: MOTION.durationMedium,
} as const;

export const revealKeyframes = {
  '@keyframes gcReveal': {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
} as const;

export const revealItemSx = (index: number) => ({
  animation: `gcReveal ${MOTION.durationMedium}ms ${MOTION.easeOut} both`,
  animationDelay: `${Math.min(index, REVEAL.maxIndex) * REVEAL.stepMs}ms`,
});

/**
 * Extended entrance vocabulary — the staged "loaded" reveals from the Loading
 * States design, centralized here so every loading surface speaks the same
 * motion language as `gcReveal`:
 *   • popIn   — cards rise + settle their scale (hardware / shipment cards)
 *   • countUp — stat numbers tick up into place (dashboard / fleet stats)
 *   • growBar — chart bars grow from their baseline
 *   • blurUp  — image-like tiles sharpen into focus (capture galleries)
 *   • dropIn  — map pins drop from above
 *   • pulse   — the "Loading…" indicator dot (infinite)
 *   • stripe  — determinate progress fill, moving diagonal stripes (infinite)
 *
 * Each staged `*ItemSx(index)` is SELF-CONTAINED: unlike `revealItemSx`, it
 * carries its own `prefers-reduced-motion` fallback (animation off, element
 * settled at its resting state), so callers may spread it unconditionally
 * without separately gating on `prefersReducedMotion`. Spread the matching
 * `*Keyframes` onto a container once to register the @keyframes. The infinite
 * helpers (`pulseDotSx`, `stripedFillSx`) bundle their own keyframes.
 *
 * All staged reveals share the capped `REVEAL` stagger (so a long list never
 * makes the user wait) and brand `MOTION.easeOut` (motion reads as a precise
 * instrument settling, never bouncy).
 */
const staggerDelayMs = (index: number) =>
  `${Math.min(Math.max(index, 0), REVEAL.maxIndex) * REVEAL.stepMs}ms`;

/**
 * `backwards` (not `both`) is deliberate: each `to` keyframe IS the element's
 * natural resting state, so reverting after the run is seamless — and, unlike a
 * `forwards`/`both` fill, it does not leave a filled `transform`/`filter`
 * lingering over the element, which would otherwise clobber `:hover` lifts on
 * cards and pins. During the stagger delay it holds the hidden `from` state.
 */
const STAGED = `${MOTION.durationMedium}ms ${MOTION.easeOut} backwards`;

// ── popIn — cards ───────────────────────────────────────────────────────────
export const popInKeyframes = {
  '@keyframes gcPopIn': {
    from: { opacity: 0, transform: 'translateY(14px) scale(0.99)' },
    to: { opacity: 1, transform: 'translateY(0) scale(1)' },
  },
} as const;

export const popInItemSx = (index = 0) => ({
  animation: `gcPopIn ${STAGED}`,
  animationDelay: staggerDelayMs(index),
  [REDUCED_MOTION]: { animation: 'none', opacity: 1, transform: 'none' },
});

// ── countUp — stat numbers ──────────────────────────────────────────────────
export const countUpKeyframes = {
  '@keyframes gcCountUp': {
    from: { opacity: 0, transform: 'translateY(9px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
} as const;

export const countUpItemSx = (index = 0) => ({
  animation: `gcCountUp ${STAGED}`,
  animationDelay: staggerDelayMs(index),
  [REDUCED_MOTION]: { animation: 'none', opacity: 1, transform: 'none' },
});

// ── growBar — chart bars (grow from baseline) ───────────────────────────────
export const growBarKeyframes = {
  '@keyframes gcGrowBar': {
    from: { transform: 'scaleY(0.55)' },
    to: { transform: 'scaleY(1)' },
  },
} as const;

export const growBarItemSx = (index = 0) => ({
  transformOrigin: 'bottom',
  animation: `gcGrowBar ${STAGED}`,
  animationDelay: staggerDelayMs(index),
  [REDUCED_MOTION]: { animation: 'none', transform: 'none' },
});

// ── blurUp — capture tiles sharpen into focus ───────────────────────────────
export const blurUpKeyframes = {
  '@keyframes gcBlurUp': {
    from: { opacity: 0, filter: 'blur(10px)', transform: 'scale(1.04)' },
    to: { opacity: 1, filter: 'blur(0)', transform: 'scale(1)' },
  },
} as const;

export const blurUpItemSx = (index = 0) => ({
  animation: `gcBlurUp ${STAGED}`,
  animationDelay: staggerDelayMs(index),
  [REDUCED_MOTION]: {
    animation: 'none',
    opacity: 1,
    filter: 'none',
    transform: 'none',
  },
});

// ── dropIn — map pins drop from above ───────────────────────────────────────
export const dropInKeyframes = {
  '@keyframes gcDropIn': {
    from: { opacity: 0, transform: 'translateY(-9px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
} as const;

export const dropInItemSx = (index = 0) => ({
  animation: `gcDropIn ${STAGED}`,
  animationDelay: staggerDelayMs(index),
  [REDUCED_MOTION]: { animation: 'none', opacity: 1, transform: 'none' },
});

// ── pulse — the "Loading…" indicator dot (infinite, self-contained) ─────────
export const pulseDotSx = {
  '@keyframes gcPulse': {
    '0%, 100%': { opacity: 0.45 },
    '50%': { opacity: 1 },
  },
  'animation': 'gcPulse 1.1s ease-in-out infinite',
  [REDUCED_MOTION]: { animation: 'none', opacity: 0.7 },
} as const;

// ── stripe — determinate progress fill, moving stripes (infinite) ───────────
/** Spread onto a Signal-Green determinate progress fill to overlay the moving
 *  diagonal stripes. The fill owns its `width` + `backgroundColor`; this adds
 *  the animated stripe texture. Settles to a flat fill under reduced motion. */
export const stripedFillSx = {
  '@keyframes gcStripe': {
    from: { backgroundPosition: '0 0' },
    to: { backgroundPosition: '32px 0' },
  },
  'backgroundImage':
    'repeating-linear-gradient(115deg, rgba(255,255,255,0) 0 14px, rgba(255,255,255,0.22) 14px 18px)',
  'backgroundSize': '32px 100%',
  'animation': 'gcStripe 0.7s linear infinite',
  [REDUCED_MOTION]: { animation: 'none' },
} as const;

/** Side navigation (navbar rail) — Deep Navy ground, Signal Green active item. */
export const NAV = {
  bg: COLORS.frameNavy,
  activeBg: COLORS.signalGreen,
  activeFg: COLORS.cargoBlack,
  iconRest: COLORS.onFrame,
  iconActive: COLORS.cargoBlack,
  hairline: HAIRLINE.onNavy,

  // ── Sectioned sidebar (SideNav — labelled, foldable groups) ───────────────
  /** Resting / compact (<1500px) widths of the labelled rail. */
  width: 224,
  widthCompact: 208,
  /** Collapsed icon-only rail width (whole-panel collapse). */
  widthCollapsed: 64,
  /** Left indent + guide hairline for sub-items under a section header. */
  itemIndent: 12,
  itemGuide: 'rgba(255, 255, 255, 0.09)',
  /** Item-row + active-fill corner radius (the rounded-square selection motif). */
  itemRadius: RADII.chip, // 10
  /** Item label colours: white at rest, Cargo Black when active (on green). */
  itemLabelRest: 'rgba(255, 255, 255, 0.92)',
  /** Hover / press washes on the navy ground (no colour, just lift). */
  hoverBg: 'rgba(255, 255, 255, 0.06)',
  pressBg: 'rgba(255, 255, 255, 0.10)',
  /** Section (group) header — mono eyebrow label + category glyph. */
  sectionLabel: 'rgba(255, 255, 255, 0.52)',
  sectionLabelActive: COLORS.onFrame,
  sectionGlyphRest: 'rgba(255, 255, 255, 0.68)',
  /** The "you are here" dot + active-section glyph (Signal Green selection). */
  marker: COLORS.signalGreen,
  /** Collapsed-section item count chip (≥4.5:1 on navy for AA). */
  countFg: 'rgba(255, 255, 255, 0.56)',
  /** Optional per-item badge (e.g. alert count). */
  badgeBg: 'rgba(255, 255, 255, 0.14)',
  badgeFg: COLORS.onFrame,
  /** Beta-status capsule on section headers. */
  betaBg: 'rgba(133, 184, 255, 0.12)',
  betaBorder: 'rgba(133, 184, 255, 0.36)',
  betaFg: COLORS.statusInfoOnDark,
  /** Active-item badge — a Cargo Black wash on the green fill. */
  activeBadgeBg: 'rgba(10, 10, 10, 0.16)',
  /** Pinned partner footer (Mega Fortris). */
  footerLabel: 'rgba(255, 255, 255, 0.38)',
  footerName: 'rgba(255, 255, 255, 0.82)',
} as const;

/**
 * Button surface tokens for `sx`-based buttons that don't go through the MUI
 * `variant` overrides. The MUI theme styles `contained` (green pill) and
 * `outlined` (navy) globally; use these only for bespoke buttons.
 */
export const BUTTON = {
  primary: {
    backgroundColor: COLORS.signalGreen,
    color: COLORS.signalGreenInk,
    borderRadius: `${RADII.pill}px`,
    hoverBackgroundColor: COLORS.signalGreenHover,
  },
  secondary: {
    backgroundColor: COLORS.surfaceCard,
    color: COLORS.navy,
    border: `1px solid ${COLORS.borderSoft}`,
    borderRadius: `${RADII.pill}px`,
  },
  danger: {
    backgroundColor: COLORS.statusAlert,
    color: '#FFFFFF',
    borderRadius: `${RADII.pill}px`,
  },
  disabled: {
    backgroundColor: COLORS.disabledBg,
    color: COLORS.borderStrong,
  },
} as const;

/**
 * The one action-menu icon geometry — spread as `style={ACTION_ICON_STYLE}`
 * on the SVG icon of a table row-action entry instead of re-typing the
 * width/margin literals per page.
 */
export const ACTION_ICON_STYLE = {
  width: '18px',
  marginRight: '8px',
} as const;

/**
 * Soft-tint background: a status/device hue at low alpha (~14% default), per the
 * DESIGN.md Soft-Tint rule. Accepts a 3- or 6-digit hex.
 */
const CSS_VAR_FALLBACK_HEX = /var\([^,]+,\s*(#[0-9a-fA-F]{3,6})\s*\)/;

export const softTint = (color: string, alpha = 0.14): string => {
  if (color.startsWith('var(')) {
    const percentage = Number((alpha * 100).toFixed(2));
    return `color-mix(in srgb, ${color} ${percentage}%, transparent)`;
  }

  const fallbackHex = color.match(CSS_VAR_FALLBACK_HEX)?.[1];
  const hex = fallbackHex ?? color;
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) {
    const percentage = Number((alpha * 100).toFixed(2));
    return `color-mix(in srgb, ${color} ${percentage}%, transparent)`;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Dashboard device-kind icon chips — green at rest, caution amber when that
 *  kind has an alert. (Was a rogue #FF8000 that matched no token; caution is
 *  the semantic "attention" hue — Alert Red stays reserved for breaches.) */
export const DEVICE_KIND_CHIP = {
  ok: {
    iconColor: COLORS.statusGoodInk,
    labelColor: COLORS.statusGoodInk,
    bg: softTint(COLORS.statusGood),
  },
  alert: {
    iconColor: COLORS.statusCautionInk,
    labelColor: COLORS.statusCautionInk,
    bg: softTint(COLORS.statusCaution),
  },
} as const;

export const deviceKindChipColors = (inAlert: boolean) =>
  inAlert ? DEVICE_KIND_CHIP.alert : DEVICE_KIND_CHIP.ok;

/**
 * Canonical status/severity tone map (the Soft-Tint Rule, one place).
 * `text` is the darkened ink (AA on white/tint), `hue` the vivid dot/icon
 * color, `tint` the ~14% soft background. Severity naming convention:
 * good=healthy, info=transitional, caution=warning/attention-soon,
 * alert=ACTION_REQUIRED/breach/critical, neutral=unknown/none.
 * Use this instead of re-declaring per-file severity color maps.
 */
export const STATUS_TONE = {
  good: {
    text: COLORS.statusGoodInk,
    hue: COLORS.statusGood,
    tint: softTint(COLORS.statusGood),
  },
  info: {
    text: COLORS.statusInfoInk,
    hue: COLORS.statusInfo,
    tint: softTint(COLORS.statusInfo),
  },
  caution: {
    text: COLORS.statusCautionInk,
    hue: COLORS.statusCaution,
    tint: softTint(COLORS.statusCaution),
  },
  alert: {
    text: COLORS.statusAlertInk,
    hue: COLORS.statusAlert,
    tint: softTint(COLORS.statusAlert),
  },
  neutral: {
    text: COLORS.inkSecondary,
    hue: COLORS.slate,
    tint: softTint(COLORS.slate, 0.12),
  },
} as const;

export type StatusToneKey = keyof typeof STATUS_TONE;

/**
 * Canonical categorical CHART series palette — the one "categorical theme" for
 * every multi-series chart (doughnut segments, multi-line/bar breakdowns) across
 * the app. The four device-family brand hues in a FIXED order: assign by index
 * and NEVER re-cycle when a series is filtered out (color follows the entity, not
 * its rank). These are flat `RAW_COLORS` (hex) because Chart.js canvas cannot
 * resolve the CSS `var()` strings in `COLORS.*`.
 *
 * Validated with the dataviz palette checker against both chart surfaces
 * (light #FFFFFF, dark #172234): lightness band, chroma floor, adjacent-pair CVD
 * separation (worst ΔE 90.3), and ≥3:1 contrast — ALL PASS on light; on dark two
 * hues drop below 3:1 (relief satisfied by the always-present legend + tooltips).
 *
 * IMPORTANT — status colors are NOT part of this palette. Charts whose color
 * MEANS good/caution/alert (status breakdowns, alert severities, online/offline)
 * use `STATUS_TONE` / the `RAW_COLORS.status*` hues instead, because a status
 * color follows state, never series identity. Reusing a status hue as "series 2"
 * makes a category impersonate a state — do not do it.
 *
 * Past 4 series, fold the tail into an "Other" slot or facet into small
 * multiples rather than cycling into a repeated hue.
 */
export const CHART_SERIES: readonly string[] = [
  RAW_COLORS.deviceImlock, // #1E6BB8 blue
  RAW_COLORS.deviceImsecure, // #B5651D earthy orange
  RAW_COLORS.deviceImcontrol, // #5C3FC7 violet
  RAW_COLORS.deviceImsense, // #1F9454 green
] as const;

/** Categorical series color by index (fixed assignment; see `CHART_SERIES`). */
export const chartSeriesColor = (index: number): string =>
  CHART_SERIES[index % CHART_SERIES.length];

/**
 * The one Signal-Green switch — green track + white thumb when checked (the
 * app-wide convention). Spread onto `<Switch sx={SWITCH_SX}>` instead of
 * re-declaring the green switch per file.
 */
export const SWITCH_SX = {
  '& .MuiSwitch-switchBase.Mui-checked': {
    'color': COLORS.surfaceCard,
    '& + .MuiSwitch-track': {
      backgroundColor: COLORS.signalGreen,
      opacity: 1,
    },
  },
} as const;

/** Mono eyebrow label — modal system (IBM Plex Mono 11px/700, uppercase, 0.22em, slate). */
export const EYEBROW_SX = {
  fontFamily: FONTS.mono,
  fontWeight: 700,
  fontSize: TYPE_SCALE.micro,
  textTransform: 'uppercase',
  letterSpacing: '0.22em',
  color: COLORS.inkSecondary,
} as const;
